// @flow
import React from "react";
import shallowCompare from "react-addons-shallow-compare";
import { connect } from "react-redux";
import Sticky from "react-stickynode";
import Waypoint from "react-waypoint";
import BookmarkButtonAlt from "backpack-ui/dist/components/bookmarkButtonAlt";
import Breadcrumbs from "./breadcrumbs";
import ContentBlock from "./contentBlock";
import PageHeader from "./pageHeader";
import EssentialInfo from "./essentialInfo";
import ContentHeader from "./contentHeader";
import { connected as NearbyPlaces } from "./nearbyPlaces";
import Callout from "./callout";
import EditLink from "./editLink";
import ImagesContainer from "./imagesContainer";
import ShareMenu from "./shareMenu";
import ContactBar from "./contactBar";
import FeatureDiscover from "./featureDiscover";
import DiscoveryFlyout from "./featureDiscover/discoveryFlyout";
import { connected as Booking } from "./booking";
import RelatedTour from "./relatedTour";
import styles from "./styles";
import { createListUrl, createDetailUrlFromResource } from "../../../../server/utils/createUrls";
import { poiTypes } from "../../../../server/lib/open-planet/poiTypes";
import DataLayer from "../../shared/components/dataLayer";
import editUrl from "../utils/editUrl";
import cleanUp from "../utils/cleanUp";
import { getToursAndActivities } from "../selectors/selectActivity";
import type { Poi, Place, Breadcrumb, WaypointLeaveEvent, MobileState } from "../../../../server/types";

export type RestaurantPoiComponentProps = {
  poi: Poi,
  place: Place,
  images: Array<string>,
  breadcrumb: Array<Breadcrumb>,
  mobile: MobileState,
  activity: Object,
  tour: Object,
  params: Object,
  onClickBookmark: Function,
  marked: Boolean,
};

export type RestaurantPoiComponentState = {
  nearby: boolean,
  hideMap: boolean,
};

/**
 * Renders a bookable restaurant POI
 */
export class RestaurantPoiComponent extends React.Component {
  constructor() {
    super();

    this.state = {
      nearby: false,
      hideMap: false,
    };

    this.getPoiUrl = this.getPoiUrl.bind(this);
    this.enterNearbyPlaces = this.enterNearbyPlaces.bind(this);
    this.leaveNearbyPlaces = this.leaveNearbyPlaces.bind(this);
  }

  state: RestaurantPoiComponentState;

  shouldComponentUpdate(
    nextProps: RestaurantPoiComponentProps,
    nextState: RestaurantPoiComponentState
  ) {
    return shallowCompare(this, nextProps, nextState);
  }

  getPoiUrl: () => string;

  getPoiUrl() {
    const { poi, params, breadcrumb } = this.props;

    return createDetailUrlFromResource(poi, breadcrumb.map(b => b.title), params.placeId);
  }

  props: RestaurantPoiComponentProps;
  enterNearbyPlaces: () => void;
  leaveNearbyPlaces: () => void;

  enterNearbyPlaces() {
    this.setState({
      hideMap: true,
    });
  }

  leaveNearbyPlaces(props: WaypointLeaveEvent) {
    this.setState({
      hideMap: (props.currentPosition === "above"),
    });
  }

  render() {
    const { poi, place, images, activity, tour, params, breadcrumb } = this.props;
    const subtype = poiTypes[poi.poiType.toLowerCase()].displayName;

    if (poi.review) {
      Object.keys(poi.review).forEach((review) => {
        poi.review[review] = cleanUp.removeBackslash(poi.review[review]);
      });
    }

    const tinyHeading = {
      level: 4,
      size: "tiny",
      weight: "thick",
      importance: "high",
      caps: true,
    };

    const name = poi.name;
    const poiId = `poi-${poi.id}`;
    const categoryLink = createListUrl({
      resource: poi.type,
      kind: poi.poiType,
      breadcrumb: breadcrumb.map(b => b.title),
      placeId: place.id,
    });

    const currentBreadcrumb = {
      title: subtype,
      href: categoryLink,
    };

    return (
      <div className="PageContainer" id={poiId} key={poiId}>
        <DataLayer
          data={{
            cd26_PartnerOfferingDetails: name,
            cd27_PartnerOfferingKind: poi.subtypes.join(","),
            cd28_PartnerName: "",
          }}
        />

        <Breadcrumbs
          links={[...breadcrumb, currentBreadcrumb]}
        />

        <div className="Page Page--detail">
          <PageHeader
            heading={name}
            title={`${place.name} ${subtype}`}
            titleHref={categoryLink}
            topChoice={poi.poiAttributes && poi.poiAttributes.length &&
              poi.poiAttributes[0].name === "top_choice"}
            type={poi.subtypes[0]}
            place={poi.containingPlace.name}
            alignment="center"
            contained
          />

          <div className="PageContent">
            {this.props.marked !== null &&
              <div>
                <BookmarkButtonAlt
                  className="js-gtm-bookmark-button"
                  onClick={this.props.onClickBookmark}
                  marked={this.props.marked}
                  style={styles.bookmarkButtonAlt}
                />
                <FeatureDiscover
                  featureName="firstPoiSeen"
                  removeOnOuterClick
                  removeOnNavigate
                >
                  <DiscoveryFlyout
                    headline="Save this place"
                    description="Tap the bookmark to save and organize your interests into lists."
                    style={styles.featureDiscovery.button}
                  />
                </FeatureDiscover>
              </div>
            }

            {images && images.length > 0 && this.props.mobile &&
              <ImagesContainer images={images} mobile />
            }

            <aside className="PageAside" data-hide-map={this.state.hideMap}>
              <Sticky
                enabled={!this.props.mobile}
                top={30}
                bottomBoundary={`#${poiId} .PageContent`}
              >
                {!this.props.mobile && false &&
                  <Booking
                    type="restaurant"
                  />
                }

                <EssentialInfo
                  poi={poi}
                  place={place}
                  mobile={this.props.mobile}
                  links={breadcrumb}
                />

                {(poi.telephone || poi.url) &&
                  <ContactBar
                    phone={poi.telephone}
                    website={poi.url}
                  />
                }

                {this.props.mobile && false &&
                  <Booking
                    type="restaurant"
                  />
                }
              </Sticky>
            </aside>

            <article className="PageArticle">
              {images && images.length > 0 && !this.props.mobile &&
                <ImagesContainer images={images} />
              }

              <div className="PageArticle-content">
                <article>
                  {poi.review &&
                    <ContentBlock
                      heading=""
                      htmlContent={poi.review.essential}
                    />}

                  {activity &&
                    <Callout
                      type={activity.type}
                      heading={activity.name}
                      slug={createDetailUrlFromResource(
                        activity.poi,
                        breadcrumb.map(b => b.title),
                        params.placeId
                      )}
                      price={{
                        currency: activity.currency,
                        amount: activity.price,
                      }}
                      image={activity.image}
                      category={activity.category}
                    />}

                  {poi.review && poi.review.extension && <ContentBlock
                    heading=""
                    htmlContent={poi.review.extension}
                  />}

                  <footer className="ContentFooter">
                    <div className="ContentFooter-text">
                      <EditLink url={editUrl(poi, place, breadcrumb)} />
                    </div>

                    <div className="ContentFooter-share">
                      <ShareMenu
                        text={poi.name}
                        url={`https://www.lonelyplanet.com${this.getPoiUrl()}`}
                        mobile={this.props.mobile}
                      />
                    </div>
                  </footer>
                </article>

                <aside>
                  {poi.location &&
                    <section className="ContentSection ContentSection-nearbyPlaces">
                      <ContentHeader
                        title="Location &amp; nearby places"
                        heading={tinyHeading}
                        border="bottom"
                      />

                      <NearbyPlaces
                        currentPlace={poi}
                        mapId={`leaflet-map-container-${poi.id}`}
                        relatedPlaceId={params.placeId}
                      />

                      <Waypoint
                        onEnter={this.enterNearbyPlaces}
                        onLeave={this.leaveNearbyPlaces}
                        fireOnRapidScroll
                      />
                    </section>
                  }

                  {tour &&
                    <section className="ContentSection ContentSection-relatedTour">
                      <ContentHeader
                        title="Related Tour"
                        heading={tinyHeading}
                        border="bottom"
                        moreLink={{
                          href: createListUrl({
                            placeId: place.id,
                            resource: "partner_activity",
                            kind: "tours",
                            breadcrumb: breadcrumb.map(b => b.title),
                          }),
                          size: "small",
                          children: "View all tours",
                        }}
                      />

                      <RelatedTour
                        title={tour.name}
                        slug={createDetailUrlFromResource(
                          tour.poi,
                          breadcrumb.map(b => b.title),
                          params.placeId
                        )}
                        tripLength={tour.duration}
                        price={{
                          amount: tour.price,
                          currency: tour.currency,
                        }}
                        image={tour.image}
                        destination={`${tour.departs} to ${tour.arrives}`}
                        reviews={tour.reviews}
                      />
                    </section>
                  }
                </aside>
              </div>
            </article>
          </div>
        </div>
      </div>
    );
  }
}

RestaurantPoiComponent.propTypes = {
  /**
   * The POI for this component
   */
  poi: React.PropTypes.object.isRequired,

  /**
   * The place data for the POI
   */
  place: React.PropTypes.object.isRequired,

  /**
   * An array of images
   */
  images: React.PropTypes.array.isRequired,

  /**
   * The breadcrumbs for this component
   */
  breadcrumb: React.PropTypes.array.isRequired,

  /**
   * Whether or not the layout should be formatted for mobile screen sizes
   */
  mobile: React.PropTypes.bool,

  /**
   * Activity POI related to current restaurant POI
   */
  activity: React.PropTypes.object,

  /**
   * Tour POI related to current restaurant POI
   */
  tour: React.PropTypes.object,

  /**
   * React Router params
   */
  params: React.PropTypes.object,
  onClickBookmark: React.PropTypes.func,
  marked: React.PropTypes.bool,
};

const mapStateToProps = (state) => ({
  mobile: state.mobile.isMobile,
  activity: getToursAndActivities(state).activity,
  tour: getToursAndActivities(state).tour,
  breadcrumb: state.breadcrumb,
});

const connected = connect(mapStateToProps)(RestaurantPoiComponent);
export { connected };



// WEBPACK FOOTER //
// ./assets/pois/components/restaurant.jsx